import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ClipboardCheck, CalendarCheck2 } from "lucide-react";
import { openCalendly } from "@/integrations/calendly";

export default function LeadForm() {
  return (
    <section id="contact" data-testid="lead-form-section" className="relative py-20 md:py-28 bg-[#0B0F19] overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[360px] w-[760px] rounded-full bg-indigo-600/10 blur-[130px]" />

      <div className="relative max-w-3xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
            Ready to See Your <span className="text-gradient">Lead Flow Clearly?</span>
          </h2>
          <p className="mt-5 text-lg md:text-xl text-slate-200 leading-relaxed">
            Start with a free, personally reviewed audit of your lead flow — or book a 30-minute
            review call directly. Either way, you'll leave knowing your next step.
          </p>

          <div data-testid="final-trust-chips" className="mt-7 flex flex-wrap justify-center gap-2.5">
            <span className="rounded-full border border-white/10 bg-white/[0.04] text-slate-300 text-xs font-semibold px-3.5 py-1.5">Personally reviewed findings</span>
            <span className="rounded-full border border-white/10 bg-white/[0.04] text-slate-300 text-xs font-semibold px-3.5 py-1.5">Usually within 24 hours</span>
            <span className="rounded-full border border-white/10 bg-white/[0.04] text-slate-300 text-xs font-semibold px-3.5 py-1.5">No obligation</span>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 gap-5 text-left">
            <div className="rounded-3xl border border-indigo-400/30 bg-indigo-500/[0.06] p-7 flex flex-col">
              <ClipboardCheck size={26} className="text-indigo-300 mb-4" />
              <h3 className="font-heading font-bold text-xl text-white">Free Real Estate Lead Flow Audit™</h3>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed flex-1">
                Answer a few simple questions. We review your lead flow and send personally
                reviewed findings — usually within 24 hours, and most audits are completed the same
                business day.
              </p>
              <Link
                to="/lead-flow-audit"
                data-testid="final-audit-button"
                data-track="homepage_lead_flow_audit_clicked"
                className="mt-6 inline-flex items-center justify-center rounded-full px-6 py-3.5 bg-indigo-600 hover:bg-indigo-500 font-semibold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
              >
                Get Your Free Real Estate Lead Flow Audit
              </Link>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 flex flex-col">
              <CalendarCheck2 size={26} className="text-cyan-300 mb-4" />
              <h3 className="font-heading font-bold text-xl text-white">Lead Flow Review Call</h3>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed flex-1">
                30 minutes · No hard sell · We'll identify where prospective clients may be
                getting lost and determine the most practical next step.
              </p>
              <button
                type="button"
                onClick={() => openCalendly()}
                data-testid="final-review-button"
                data-track="homepage_lead_flow_review_clicked"
                className="mt-6 inline-flex items-center justify-center rounded-full px-6 py-3.5 border-2 border-cyan-400/40 text-cyan-200 hover:border-cyan-300 hover:text-cyan-100 font-semibold transition-[border-color,color,transform] duration-200 hover:-translate-y-0.5"
              >
                Book Your Lead Flow Review
              </button>
            </div>
          </div>

          <p className="mt-10 text-base text-slate-300">
            Prefer email?{" "}
            <a
              href="mailto:ismail@ivibesconsulting.com"
              data-testid="contact-email-link"
              data-track="email_click_contact"
              className="text-cyan-300 font-semibold hover:text-cyan-200 transition-colors duration-200"
            >
              ismail@ivibesconsulting.com
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
